import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

const SEARCH_DEBOUNCE_MS = 350;

/** The same pair the feed's type chips use: chosen is filled, the rest outlined. */
const CHOSEN = { bgcolor: 'secondary.main', color: 'secondary.contrastText', fontWeight: 700 };
const UNCHOSEN = {
  bgcolor: 'background.paper',
  border: '1.5px solid',
  borderColor: 'surface.borderStrong',
};

/**
 * Search and filter controls for the directory — the services and the shops
 * alike. The two pages list different things but narrow them the same way, by
 * name, by category and by neighbourhood, so they share the one form: two
 * hand-written sets of filters is how the same page ends up with a search box
 * on one and not on the other. Only the words change, through the props.
 *
 * The categories are two rows of chips rather than a select: with thirty
 * entries, a flat list is not read, it is scanned until the eye gives up. The
 * families come first, the trades of the one chosen appear under them, and a
 * family is a filter in its own right — the API includes everything filed
 * under it. Which family is open is read off the filter rather than kept as
 * state, so a shared link opens on the same two rows.
 *
 * The text field is debounced so typing does not fire a request per
 * keystroke, while the selects apply immediately. The "Chercher" button
 * applies the typed text at once rather than waiting out the debounce: it
 * changes nothing about how the filters work, but people expect a search box
 * to have a button, and waiting for something to happen by itself is where
 * they start clicking at random.
 */
function DirectoryFilters({
  filters,
  categoryOptions,
  neighborhoods,
  onChange,
  onReset,
  searchLabel = 'Un nom ou un métier',
  searchPlaceholder = 'plombier, électricien, ménage…',
  categoryLabel = 'Catégories',
  allCategoriesLabel = 'Toutes',
}) {
  const [searchDraft, setSearchDraft] = useState(filters.q);

  // Keep the field in step when the URL changes from outside (reset, back button).
  useEffect(() => {
    setSearchDraft(filters.q);
  }, [filters.q]);

  useEffect(() => {
    if (searchDraft === filters.q) {
      return undefined;
    }

    const timer = setTimeout(() => onChange('q', searchDraft), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchDraft, filters.q, onChange]);

  const activeCategory = categoryOptions.find((option) => option.slug === filters.category);

  const families = categoryOptions.filter((option) => option.isRoot);
  // Which family is open is not state of its own: it is read off the filter,
  // so a shared link opens on the same two rows.
  const openFamily = activeCategory?.isRoot ? activeCategory.slug : activeCategory?.parentSlug;
  const openFamilyLabel = families.find((family) => family.slug === openFamily)?.label ?? '';
  const trades = categoryOptions.filter((option) => !option.isRoot && option.parentSlug === openFamily);
  const activeNeighborhood = neighborhoods.find((one) => one.slug === filters.neighborhood);

  /**
   * What the reader has narrowed to, each piece removable on its own.
   *
   * The category is not repeated here: the chips above show it by being
   * pressed, and the same word twice in one panel reads as two filters.
   */
  const applied = [
    filters.q && { key: 'q', label: `« ${filters.q} »` },
    activeNeighborhood && { key: 'neighborhood', label: activeNeighborhood.name },
  ].filter(Boolean);

  return (
    <Paper
      component="form"
      variant="outlined"
      onSubmit={(event) => {
        event.preventDefault();
        onChange('q', searchDraft);
      }}
      sx={{ p: { xs: 2.5, md: 3.25 }, mb: 3.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}
    >
      {/* The families first, then the trades of the one chosen. A flat list of
          thirty entries in a select is a list nobody reads: it is scanned, and
          the eye gives up. Two rows of chips show what the quarter actually
          holds — and a family is a filter in its own right, since the API
          includes everything filed under it. */}
      {categoryOptions.length > 0 && (
        <Box>
          <Typography variant="subtitle2" component="p" sx={{ mb: 1.25 }}>
            {categoryLabel}
          </Typography>

          <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={allCategoriesLabel}
              onClick={() => onChange('category', '')}
              aria-pressed={!filters.category}
              sx={filters.category ? UNCHOSEN : CHOSEN}
            />
            {families.map((family) => {
              const chosen = openFamily === family.slug;

              return (
                <Chip
                  key={family.slug}
                  label={family.label}
                  onClick={() => onChange('category', chosen ? '' : family.slug)}
                  aria-pressed={chosen}
                  sx={chosen ? CHOSEN : UNCHOSEN}
                />
              );
            })}
          </Stack>

          {trades.length > 0 && (
            <Stack
              direction="row"
              sx={{ gap: 1, flexWrap: 'wrap', mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'surface.rule' }}
            >
              <Chip
                size="small"
                label={`Tout ${openFamilyLabel}`}
                onClick={() => onChange('category', openFamily)}
                aria-pressed={filters.category === openFamily}
                sx={filters.category === openFamily ? CHOSEN : UNCHOSEN}
              />
              {trades.map((trade) => {
                const chosen = filters.category === trade.slug;

                return (
                  <Chip
                    key={trade.slug}
                    size="small"
                    label={trade.label}
                    onClick={() => onChange('category', chosen ? openFamily : trade.slug)}
                    aria-pressed={chosen}
                    sx={chosen ? CHOSEN : UNCHOSEN}
                  />
                );
              })}
            </Stack>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr auto' },
          alignItems: 'end',
        }}
      >
        <TextField
          label={searchLabel}
          id="directory-search"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          placeholder={searchPlaceholder}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          label="Quartier"
          id="directory-neighborhood"
          // Shown only once it is among the options: the list arrives after the
          // filter, which the URL already carries, and MUI warns on a value it
          // cannot show. The filter itself stays applied meanwhile.
          value={neighborhoods.some((n) => n.slug === filters.neighborhood) ? filters.neighborhood : ''}
          onChange={(e) => onChange('neighborhood', e.target.value)}
          fullWidth
          disabled={0 === neighborhoods.length}
        >
          <MenuItem value="">Tous les quartiers</MenuItem>
          {neighborhoods.map((neighborhood) => (
            <MenuItem key={neighborhood.slug} value={neighborhood.slug}>
              {neighborhood.name}
            </MenuItem>
          ))}
        </TextField>

        <Button type="submit" variant="contained" size="large" sx={{ height: 56 }}>
          Chercher
        </Button>
      </Box>

      {applied.length > 0 && (
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1.25,
            flexWrap: 'wrap',
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'surface.rule',
          }}
        >
          <Typography variant="caption">Votre recherche&nbsp;:</Typography>
          {applied.map((one) => (
            <Chip
              key={one.key}
              label={one.label}
              onDelete={() => onChange(one.key, '')}
              sx={{ bgcolor: 'warning.light', color: 'primary.dark', fontWeight: 700 }}
            />
          ))}
          <Button onClick={onReset} variant="text" size="small" color="primary">
            Tout effacer
          </Button>
        </Stack>
      )}
    </Paper>
  );
}

export default DirectoryFilters;
